import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { API_URL } from '../services/api';

const WebSocketContext = createContext(null);

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children, user }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const userRef = useRef(user);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  // Keep userRef in sync with user prop
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Callback for notification events
  const notificationListenersRef = useRef(new Set());

  const subscribe = useCallback((listener) => {
    notificationListenersRef.current.add(listener);
    return () => notificationListenersRef.current.delete(listener);
  }, []);

  const notifyListeners = useCallback((message) => {
    notificationListenersRef.current.forEach(listener => {
      try {
        listener(message);
      } catch (error) {
        console.error('Error in WebSocket listener:', error);
      }
    });
  }, []);

  const connect = useCallback(() => {
    if (!userRef.current) return;

    // Prevent connecting if already connected or connecting
    if (wsRef.current) {
      const readyState = wsRef.current.readyState;
      if (readyState === WebSocket.CONNECTING || readyState === WebSocket.OPEN) {
        console.log('WebSocket already connected or connecting, skipping...');
        return;
      }
      // Close existing connection if it's closing or closed
      wsRef.current.close();
    }

    try {
      // WebSocket URL: ws://localhost:8000/api/ws/notifications
      // Cookie is automatically sent with the WebSocket upgrade request
      const wsUrl = `${API_URL.replace(/^http(s?)/, 'ws$1')}/ws/notifications`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle notification-specific state updates
          switch (message.type) {
            case 'connection_established':
              break;

            case 'notification_created':
              // New notification received
              setNotifications(prev => [message.notification, ...prev]);
              setUnreadCount(message.unread_count);
              break;

            case 'notification_read':
              // Notification marked as read (cross-tab sync)
              setNotifications(prev =>
                prev.map(n =>
                  n.id === message.notification_id
                    ? { ...n, is_read: message.is_read }
                    : n
                )
              );
              setUnreadCount(message.unread_count);
              break;

            case 'notifications_read_all':
              // All notifications marked as read (cross-tab sync)
              setNotifications(prev =>
                prev.map(n => ({ ...n, is_read: true }))
              );
              setUnreadCount(0);
              break;

            case 'pong':
              // Heartbeat response
              break;

            default:
              // Pass through all other message types to listeners
              break;
          }

          // Notify all listeners for ALL message types (including data events)
          // This allows pages to subscribe to events like 'event_updated', 'task_claimed', etc.
          notifyListeners(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        setIsConnected(false);
        wsRef.current = null;

        // Only attempt reconnection if not a normal closure and user still exists
        // Code 1000 = normal closure, 1001 = going away, 1005 = no status received
        const isNormalClosure = event.code === 1000 || event.code === 1001;

        if (!isNormalClosure && userRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
          const delay = reconnectDelay * Math.pow(2, reconnectAttemptsRef.current);
          reconnectAttemptsRef.current += 1;

          console.log(`WebSocket closed (code: ${event.code}). Reconnecting in ${delay}ms... (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else if (isNormalClosure) {
          console.log('WebSocket closed normally');
        }
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disconnect = useCallback(() => {
    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close WebSocket connection
    if (wsRef.current) {
      // Remove event handlers to prevent reconnection attempts
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.onopen = null;

      // Close the connection
      if (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close();
      }
      wsRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  // Send ping every 30 seconds to keep connection alive
  useEffect(() => {
    if (!isConnected || !wsRef.current) return;

    const pingInterval = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // 30 seconds

    return () => clearInterval(pingInterval);
  }, [isConnected]);

  // Connect on mount, disconnect on unmount
  // Only depend on user to avoid unnecessary reconnections
  useEffect(() => {
    if (user) {
      reconnectAttemptsRef.current = 0;
      connect();
    }
    return () => {
      disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const send = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
      } catch (error) {
        console.error('Failed to send WebSocket message:', error);
      }
    } else {
      console.warn('WebSocket not connected, cannot send message');
    }
  }, []);

  const value = useMemo(() => ({
    isConnected,
    notifications,
    unreadCount,
    subscribe,
    send,
    setNotifications,
    setUnreadCount
  }), [isConnected, notifications, unreadCount, subscribe, send, setNotifications, setUnreadCount]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
